// ===========================================
// Parent Onboarding Progress Page
// ===========================================
// Shows parents their onboarding status and progress

import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Button,
  Chip,
  Stack,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CompletedIcon,
  HourglassEmpty as PendingIcon,
  Payment as PaymentIcon,
  School as TrialIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useOnboardingProgress, useOnboardingSteps } from '../../hooks/useOnboarding';
import type { OnboardingStatus } from '../../api/onboarding.api';
import PageHeader from '../../components/common/PageHeader';

// ===========================================
// STATUS MESSAGING
// ===========================================

const statusMessages: Record<
  OnboardingStatus,
  { title: string; description: string; severity: 'info' | 'success' | 'warning' | 'error' }
> = {
  IN_PROGRESS: {
    title: 'Registration in Progress',
    description: 'Continue completing your registration steps below.',
    severity: 'info',
  },
  AWAITING_APPROVAL: {
    title: 'Under Review',
    description:
      'Your registration has been submitted and is being reviewed by the school. We will notify you once a decision has been made.',
    severity: 'info',
  },
  AWAITING_PAYMENT: {
    title: 'Payment Required',
    description:
      'Your registration has been approved! Please complete payment to finalize your enrollment.',
    severity: 'warning',
  },
  TRIAL_ACTIVE: {
    title: 'Trial Class Active',
    description:
      'Your trial class period is active. Enjoy your lessons!',
    severity: 'success',
  },
  COMPLETED: {
    title: 'Welcome!',
    description: 'Your registration is complete. You are all set!',
    severity: 'success',
  },
  REJECTED: {
    title: 'Registration Update',
    description:
      'Unfortunately your registration was not approved at this time. Please contact the school for more information.',
    severity: 'error',
  },
  ABANDONED: {
    title: 'Registration Expired',
    description:
      'Your registration has expired due to inactivity. You can start a new registration at any time.',
    severity: 'warning',
  },
};

// ===========================================
// STEP LABEL FORMATTING
// ===========================================

function formatStepLabel(step: string): string {
  const labels: Record<string, string> = {
    parentRegistration: 'Parent Details',
    studentRegistration: 'Student Details',
    adultRegistration: 'Your Details',
    customQuestions: 'Additional Questions',
    addAnotherStudent: 'Add Another Child',
    termsAcceptance: 'Terms & Conditions',
    classSelection: 'Select a Class',
    meetAndGreetScheduling: 'Meet & Greet',
    freeTrialBooking: 'Trial Class',
    paymentCollection: 'Payment',
    confirmationSummary: 'Confirmation',
  };
  return labels[step] || step.replace(/([A-Z])/g, ' $1').trim();
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function OnboardingProgressPage() {
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  // Try to get progressId from localStorage
  const progressId =
    typeof window !== 'undefined'
      ? localStorage.getItem(`mnm_onboarding_${schoolSlug}`) ?? ''
      : '';

  const {
    data: progress,
    isLoading: progressLoading,
  } = useOnboardingProgress(schoolSlug ?? '', progressId);

  const {
    data: stepsInfo,
    isLoading: stepsLoading,
  } = useOnboardingSteps(schoolSlug ?? '', progressId);

  const isLoading = progressLoading || stepsLoading;

  if (isLoading) {
    return (
      <Box>
        <PageHeader title="Registration Progress" />
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={80} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      </Box>
    );
  }

  if (!progress || !progressId) {
    return (
      <Box>
        <PageHeader title="Registration Progress" />
        <Alert severity="info">
          No active registration found. If you need to register, please visit the
          school's registration page.
        </Alert>
      </Box>
    );
  }

  const status = progress.status as OnboardingStatus;
  const msg = statusMessages[status];
  const steps = stepsInfo?.steps ?? [];
  const completedSteps = stepsInfo?.completedSteps ?? [];

  const completedStepIndex = completedSteps.length;

  return (
    <Box>
      <PageHeader
        title="Registration Progress"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Registration Progress' },
        ]}
      />

      {/* Status Alert */}
      <Alert
        severity={msg.severity}
        sx={{ mb: 3 }}
        icon={
          status === 'COMPLETED' ? (
            <CompletedIcon />
          ) : status === 'AWAITING_PAYMENT' ? (
            <PaymentIcon />
          ) : status === 'TRIAL_ACTIVE' ? (
            <TrialIcon />
          ) : (
            <PendingIcon />
          )
        }
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {msg.title}
        </Typography>
        <Typography variant="body2">{msg.description}</Typography>
      </Alert>

      {/* Progress Stepper */}
      {steps.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your Progress
            </Typography>
            <Stepper
              activeStep={completedStepIndex}
              alternativeLabel
              sx={{ mt: 2 }}
            >
              {steps.map((step) => {
                const isCompleted = completedSteps.includes(step);
                return (
                  <Step key={step} completed={isCompleted}>
                    <StepLabel>{formatStepLabel(step)}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Trial Progress (if applicable) */}
      {status === 'TRIAL_ACTIVE' && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Trial Class Progress
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={`${progress.freeTrialClassesUsed} of ${progress.freeTrialClassesAssigned} classes used`}
                color="primary"
              />
              {progress.freeTrialClassesUsed >= progress.freeTrialClassesAssigned && (
                <Typography variant="body2" color="text.secondary">
                  Trial complete — payment will be requested to continue.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Rejection Reason */}
      {status === 'REJECTED' && progress.rejectionReason && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Reason
            </Typography>
            <Typography variant="body2">{progress.rejectionReason}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Resume Button */}
      {status === 'IN_PROGRESS' && schoolSlug && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            href={`/${schoolSlug}/register?progressId=${progressId}`}
          >
            Continue Registration
          </Button>
        </Box>
      )}
    </Box>
  );
}
